import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { getTerm, useTerms } from '@/lib/branding';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { INSIGHT_TEMPLATES } from '@/lib/insightTemplates';
import TemplateThumbnail from '@/components/insight-templates/TemplateThumbnail';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import {
  FileText, Plus, BarChart3, Table, LineChart, PieChart, Calendar,
  LayoutGrid, Pencil, Trash2, Loader2, Eye, Link2, FolderOpen, FolderPlus, ChevronRight, FolderClosed, MoreHorizontal, Search, Shield, Upload, ArrowUpDown, SlidersHorizontal, List, Clock, TrendingUp, Filter, Camera, RefreshCw, Sparkles, GitCompare, FileDown, BookOpen, Wand2, Archive, CheckSquare, History
} from 'lucide-react';
import { toast } from 'sonner';
import { EmbedDialog } from '@/components/EmbedDialog';
import { VersionHistoryButton } from '@/components/VersionHistory';
import { EndorsementMenu } from '@/components/EndorsementMenu';
import { EndorsementBadge } from '@/components/EndorsementBadge';
import { useReportTabs } from '@/contexts/ReportTabsContext';
import { ImportReportDialog } from '@/components/ImportReportDialog';
import { PyramidImportDialog } from '@/components/PyramidImportDialog';
import { ImportReportStructureDialog } from '@/components/ImportReportStructureDialog';
import { ImportHistoryDialog } from '@/components/ImportHistoryDialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { ImpactWarning } from '@/components/ImpactWarning';
import { BadgeCheck, ShieldCheck, ShieldOff, Pyramid, AlertTriangle, CheckCircle2 } from 'lucide-react';
import ProcessHelp from '@/components/ProcessHelp';

const vizIcons = { table: Table, bar_chart: BarChart3, line_chart: LineChart, pie_chart: PieChart, area_chart: BarChart3 };

// BI import sources shown in the Import popup. `accept` mirrors the backend's
// ACCEPTED_EXT so users upload the right file type.
const IMPORT_SOURCES = [
  { id: 'birt', name: 'BIRT', accept: '.rptdesign,.xml', hint: 'Eclipse BIRT .rptdesign file', color: 'text-orange-400' },
  { id: 'tableau', name: 'Tableau', accept: '.twb,.twbx', hint: 'Tableau .twb / .twbx workbook', color: 'text-blue-400' },
  { id: 'powerbi', name: 'Power BI', accept: '.pbix', hint: 'Power BI .pbix file', color: 'text-yellow-400' },
  { id: 'qlik', name: 'Qlik', accept: '.qvf,.json', hint: 'Qlik .qvf app (or JSON export)', color: 'text-emerald-400' },
];

export default function ReportsPage() {
  const navigate = useNavigate();
  useTerms();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('reportViewMode') || 'cards');
  const changeViewMode = (mode) => { setViewMode(mode); localStorage.setItem('reportViewMode', mode); };
  const [models, setModels] = useState([]);
  // Create dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formVizType, setFormVizType] = useState('table');
  const [formModelId, setFormModelId] = useState('');
  const [saving, setSaving] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedReport, setEmbedReport] = useState(null);
  const [docLoading, setDocLoading] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogSel, setCatalogSel] = useState([]);
  const [catalogAi, setCatalogAi] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const { openTab } = useReportTabs();

  const saveBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(url);
  };

  const downloadDocument = async (report) => {
    setDocLoading(report.report_id);
    try {
      const blob = await api.downloadReportDocument(report.report_id);
      saveBlob(blob, `${(report.name || 'insight').replace(/[^a-z0-9 _-]/gi, '')} - documentation.pdf`);
      toast.success('Documentation ready');
    } catch (e) { toast.error(e.message || 'Could not generate document'); }
    finally { setDocLoading(null); }
  };

  const [exportingId, setExportingId] = useState(null);
  const exportReportStructure = async (report, includeModel = false) => {
    setExportingId(report.report_id);
    try {
      const bundle = await api.exportReport(report.report_id, includeModel);
      const fname = (report.name || 'report').replace(/[^a-z0-9 _-]/gi, '').trim().replace(/\s+/g, '_') || 'report';
      saveBlob(new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' }), `${fname}.inzara-report.json`);
      toast.success(includeModel ? 'Report + model exported' : 'Report exported');
    } catch (e) { toast.error(e.message || 'Export failed'); }
    finally { setExportingId(null); }
  };

  const generateCatalog = async () => {
    setCatalogLoading(true);
    try {
      const blob = await api.downloadReportCatalog(catalogSel, catalogAi);
      saveBlob(blob, `insight-catalog.pdf`);
      toast.success('Catalog generated');
      setCatalogOpen(false);
    } catch (e) { toast.error(e.message || 'Could not generate catalog'); }
    finally { setCatalogLoading(false); }
  };
  // Folders
  const [folders, setFolders] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const refreshReports = useCallback(() => {
    api.getReports(showArchived)
      .then(r => setReports(showArchived ? (r || []).filter(x => x.archived) : r))
      .catch(() => {});
  }, [showArchived]);
  // Keep the stage badges live when a report is promoted elsewhere (e.g. the
  // builder's Promote dialog) — refetch so the list reflects Test/Production.
  useEffect(() => {
    const onPromoted = () => refreshReports();
    window.addEventListener('inzara:report-promoted', onPromoted);
    return () => window.removeEventListener('inzara:report-promoted', onPromoted);
  }, [refreshReports]);
  // A promotion done on another route (builder/viewer) dispatches the event
  // while this list is unmounted, so the live listener above misses it. The
  // dialog also persists a `inzara:reports-dirty` flag; drain it whenever the
  // list regains focus / becomes visible / is re-navigated to, so the stage
  // badges update WITHOUT the user having to leave and come back.
  const location = useLocation();
  useEffect(() => {
    const drainIfDirty = () => {
      if (sessionStorage.getItem('inzara:reports-dirty')) {
        sessionStorage.removeItem('inzara:reports-dirty');
        refreshReports();
      }
    };
    drainIfDirty();
    const onFocus = () => drainIfDirty();
    const onVis = () => { if (document.visibilityState === 'visible') drainIfDirty(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => { window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onVis); };
  }, [refreshReports, location.pathname, location.key]);
  // Bulk selection + pack export
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [includeModelsExport, setIncludeModelsExport] = useState(false);
  const [packBusy, setPackBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSelection = () => { setSelectedIds(new Set()); setSelectMode(false); setIncludeModelsExport(false); };
  const exportSelectedPack = async () => {
    const ids = [...selectedIds];
    if (!ids.length) { toast.error('Select at least one report'); return; }
    setPackBusy(true);
    try {
      const pack = await api.exportReportPack(ids, includeModelsExport);
      saveBlob(new Blob([JSON.stringify(pack, null, 2)], { type: 'application/json' }), `reports-pack-${ids.length}.inzara-pack.json`);
      toast.success(`Exported ${ids.length} report${ids.length > 1 ? 's' : ''}${includeModelsExport ? ' + models' : ''}`);
      clearSelection();
    } catch (e) { toast.error(e.message || 'Export failed'); }
    finally { setPackBusy(false); }
  };
  const handleImported = (res) => {
    if (Array.isArray(res?.imported)) {
      refreshReports();
      const n = res.imported.length, s = (res.skipped || []).length;
      toast.success(`Imported ${n} report${n !== 1 ? 's' : ''}${s ? ` · ${s} skipped` : ''}`);
      return;
    }
    refreshReports();
    if (res?.report_id) { openTab(res.report_id); navigate(`/reports/builder?id=${res.report_id}`); }
  };
  const [userMap, setUserMap] = useState({});
  const [activeFolder, setActiveFolder] = useState(null); // null = all, 'unfiled' = no folder, folder_id
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParent, setNewFolderParent] = useState(null);
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [moveReportId, setMoveReportId] = useState(null);
  const [draggedReportId, setDraggedReportId] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [folderSearch, setFolderSearch] = useState('');
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permFolderId, setPermFolderId] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // AI Build Dashboard state
  const [aiBuildOpen, setAiBuildOpen] = useState(false);
  const [aiBuildModelId, setAiBuildModelId] = useState('');
  const [aiBuildPrompt, setAiBuildPrompt] = useState('');
  const [aiBuildLoading, setAiBuildLoading] = useState(false);
  const [aiBuildMode, setAiBuildMode] = useState('dashboard');
  // v2 plan-preview step state
  const [aiBuildStep, setAiBuildStep] = useState('compose'); // 'compose' | 'review'
  const [aiBuildPlan, setAiBuildPlan] = useState(null); // { title, description, audience, intents[] }
  const [aiBuildVocab, setAiBuildVocab] = useState(null); // { dimension_count, measure_count, measures[], dimensions[] }

  // BI Import state (BIRT / Tableau / Power BI / Qlik)
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [importPopupOpen, setImportPopupOpen] = useState(false); // source picker
  const [importSource, setImportSource] = useState(null); // birt|tableau|powerbi|qlik
  const [importParsing, setImportParsing] = useState(false);
  const [importParsed, setImportParsed] = useState(null);
  const [importModelId, setImportModelId] = useState('');
  const [importing, setImporting] = useState(false);
  const [pyramidImportOpen, setPyramidImportOpen] = useState(false);
  const [resyncingId, setResyncingId] = useState(null);
  const resyncPyramid = async (report) => {
    setResyncingId(report.report_id);
    try {
      const res = await api.resyncPyramid({ report_id: report.report_id });
      const manual = (res.review?.formulas || []).filter(f => f.status === 'manual').length;
      const unmatched = (res.review?.unmatched || []).length;
      toast.success(`Re-synced "${res.name}" — ${res.visuals_created} visual(s)${unmatched ? `, ${unmatched} to map` : ''}${manual ? `, ${manual} formula(s) manual` : ''}`);
    } catch (e) { toast.error(e.message || 'Re-sync failed'); }
    finally { setResyncingId(null); }
  };
  // Search, filter, sort
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'dashboard', 'report'
  // Endorsement filter — persisted so teams can pin "Certified only" as default.
  // Values: 'all' | 'endorsed' (cert+promoted) | 'certified' | 'promoted' | 'unendorsed' (admin only)
  const [endorsementFilter, setEndorsementFilter] = useState(() => localStorage.getItem('insightsEndorsementFilter') || 'all');
  const changeEndorsementFilter = (v) => { setEndorsementFilter(v); localStorage.setItem('insightsEndorsementFilter', v); };
  // Stage filter — persisted; defaults to the user's "hide dev/test" preference.
  const [stageFilter, setStageFilter] = useState(() => localStorage.getItem('insightsStageFilter') || 'all');
  const changeStageFilter = (v) => { setStageFilter(v); localStorage.setItem('insightsStageFilter', v); };
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'name_asc', 'name_desc'

  const { user } = useAuth();
  const isAdmin = ['admin', 'tenant_admin', 'super_admin'].includes(user?.role || '');
  const canSeeNonprod = (user?.permissions || []).some(p => p === 'reports:view_test' || p === 'reports:view_dev');
  useEffect(() => {
    if (!localStorage.getItem('insightsStageFilter') && user?.preferences?.hide_nonprod_reports) {
      setStageFilter('production');
    }
  }, [user]);

  useEffect(() => {
    api.getUsers().then(list => {
      const map = {};
      (list || []).forEach(u => { map[u.user_id] = u.name || u.email; });
      setUserMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([api.getReports(showArchived), api.getSemanticModels(), api.listFolders()])
      .then(([r, m, f]) => {
        setReports(showArchived ? (r || []).filter(x => x.archived) : r);
        setModels(m); setFolders(f || []); if (m.length > 0) setFormModelId(m[0].model_id);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [showArchived]);

  const unarchiveReport = async (report) => {
    try {
      await api.unarchiveReport(report.report_id);
      setReports(xs => xs.filter(x => x.report_id !== report.report_id));
      toast.success('Restored to your Insights list');
    } catch (e) { toast.error(e.message || 'Could not restore'); }
  };

  const openCreate = () => {
    setFormName(''); setFormDesc(''); setFormVizType('table');
    setFormModelId(models.length > 0 ? models[0].model_id : '');
    setDialogOpen(true);
  };

  const openEdit = (report) => {
    openTab(report.report_id);
    navigate(`/reports/builder?id=${report.report_id}`);
  };

  // ── AI Build Dashboard v2 — two-step flow ──
  // Step 1: generate plan from prompt; show editable intent cards.
  // Step 2: user edits/approves; execute the plan to build the dashboard.

  const resetAiBuild = () => {
    setAiBuildOpen(false);
    setAiBuildStep('compose');
    setAiBuildPrompt('');
    setAiBuildPlan(null);
    setAiBuildVocab(null);
  };

  const handleGeneratePlan = async () => {
    if (!aiBuildPrompt.trim() || !aiBuildModelId) return;
    setAiBuildLoading(true);
    try {
      const result = await api.aiBuildDashboardPlan({ prompt: aiBuildPrompt, model_id: aiBuildModelId });
      if (!result.plan) { toast.error('Plan generation returned no intents'); return; }
      setAiBuildPlan(result.plan);
      setAiBuildVocab(result.vocab_summary || null);
      setAiBuildStep('review');
    } catch (err) { toast.error(err.message); }
    finally { setAiBuildLoading(false); }
  };

  const handleAiBuildDashboard = async () => {
    // Stage 2: execute the (possibly edited) plan and create the dashboard.
    if (!aiBuildPlan || !aiBuildModelId) return;
    if (!aiBuildPlan.intents || aiBuildPlan.intents.length === 0) {
      toast.error('Add at least one intent before building'); return;
    }
    setAiBuildLoading(true);
    try {
      const result = await api.aiBuildDashboardExecute({ plan: aiBuildPlan, model_id: aiBuildModelId });
      if (result.error) { toast.error(result.error); return; }
      // Create the report (dashboard) — same wiring as before, just driven by
      // the v2 execute response instead of the legacy one-shot endpoint.
      const report = await api.createReport({
        name: result.title || 'AI Dashboard',
        description: result.description || '',
        visualization_type: 'dashboard',
      });
      const visuals = [];
      const vizConfigs = {};
      const gridLayouts = {};
      (result.visuals || []).forEach((v, i) => {
        const id = `v_ai_${Date.now()}_${i}`;
        visuals.push({
          id, vizType: v.viz_type || 'bar_chart', title: v.title || `Visual ${i + 1}`,
          rowDims: v.row_dims || [], colDim: v.col_dim || '',
          valueMeasures: v.value_measures || [], filterDims: [],
        });
        if (v.top_n?.enabled) {
          vizConfigs[id] = { ...(vizConfigs[id] || {}), topN: v.top_n };
        }
        if (v.viz_config && typeof v.viz_config === 'object') {
          // Planner / executor patch — e.g. { geoScope: 'us-states' } for map tiles
          vizConfigs[id] = { ...(vizConfigs[id] || {}), ...v.viz_config };
        }
        if (v.grid_layout) {
          gridLayouts[id] = v.grid_layout;
        }
      });
      await api.updateReport(report.report_id, {
        name: result.title || 'AI Dashboard',
        description: result.description || '',
        query_definition: { model_id: aiBuildModelId, dimensions: [], measures: [] },
        config: { visuals, visualConfigs: vizConfigs, gridLayouts, showGrid: true, showLegend: true, designerMode: 'dashboard' },
      });
      sessionStorage.setItem('exploreInsightConfig', JSON.stringify({
        query_definition: { model_id: aiBuildModelId },
        config: { visuals, visualConfigs: vizConfigs, gridLayouts, showGrid: true, showLegend: true },
      }));
      const skipped = (result._skipped_tiles || []).length;
      const skippedHint = skipped > 0 ? ` (${skipped} tile${skipped > 1 ? 's' : ''} couldn't be built — edit the dashboard to add them manually)` : '';
      toast.success(`Dashboard "${result.title}" created with ${visuals.length} visuals${skippedHint}`);
      resetAiBuild();
      navigate(`/dashboards/builder?id=${report.report_id}&fromExplore=true`);
    } catch (err) { toast.error(err.message); }
    finally { setAiBuildLoading(false); }
  };

  const handleAiBuildReport = async () => {
    if (!aiBuildPrompt.trim() || !aiBuildModelId) return;
    setAiBuildLoading(true);
    try {
      const result = await api.aiBuildReport({ prompt: aiBuildPrompt, model_id: aiBuildModelId });
      if (result.error) { toast.error(result.error); return; }
      // Create the report
      const report = await api.createReport({
        name: result.title || 'AI Report',
        description: result.description || '',
        visualization_type: 'report',
      });
      // Build visuals from all pages
      // pageVisuals is the source-of-truth in ReportBuilder — it holds full
      // visual objects keyed by pageId. The flat `visuals` array is kept only
      // for legacy fallback paths inside the builder; AI-built reports must
      // populate `pageVisuals` with the actual objects, not bare ids, or the
      // canvas renders empty.
      const visuals = [];
      const vizConfigs = {};
      const pages = [];
      const pageVisuals = {};
      (result.pages || []).forEach((page, pageIdx) => {
        const pageId = `page_${pageIdx + 1}`;
        pages.push({ id: pageId, name: page.name || `Page ${pageIdx + 1}` });
        pageVisuals[pageId] = [];
        (page.visuals || []).forEach((v, i) => {
          const id = `v_air_${Date.now()}_${pageIdx}_${i}`;
          const visual = {
            id, vizType: v.viz_type || 'bar_chart', title: v.title || '',
            rowDims: v.row_dims || [], colDim: v.col_dim || '',
            valueMeasures: v.value_measures || [], filterDims: [],
            freePosition: { x: v.x || 0, y: v.y || 0, w: v.w || 300, h: v.h || 200 },
            _reportPageIndex: pageIdx,
          };
          if (v.viz_type === 'rich_text') {
            visual.textContent = v.text_content || v.title || '';
            visual.textSize = v.text_size || 12;
          }
          if (v.top_n?.enabled) vizConfigs[id] = { topN: v.top_n };
          visuals.push(visual);
          pageVisuals[pageId].push(visual);
        });
      });
      // Header/footer config
      const headerCfg = result.header || {};
      const footerCfg = result.footer || {};
      // Update report with full config
      await api.updateReport(report.report_id, {
        name: result.title || 'AI Report',
        description: result.description || '',
        query_definition: { model_id: aiBuildModelId, dimensions: [], measures: [] },
        config: {
          visuals, visualConfigs: vizConfigs, showGrid: true, showLegend: true,
          designerMode: 'report',
          reportPageSize: result.page_size || 'letter',
          reportOrientation: result.orientation || 'portrait',
          reportMargins: { top: 48, right: 48, bottom: 48, left: 48 },
          reportHeader: { enabled: true, height: headerCfg.height || 60, title: headerCfg.title || result.title, repeatOnPages: true, showOnFirstPage: true },
          reportFooter: { enabled: true, height: footerCfg.height || 40 },
          pages: pages.length > 0 ? pages : [{ id: 'page_1', name: 'Page 1' }],
          pageVisuals,
        },
      });
      sessionStorage.setItem('exploreInsightConfig', JSON.stringify({
        query_definition: { model_id: aiBuildModelId },
        config: {
          visuals, visualConfigs: vizConfigs, showGrid: true, showLegend: true,
          designerMode: 'report',
          reportPageSize: result.page_size || 'letter',
          reportOrientation: result.orientation || 'portrait',
          pages: pages.length > 0 ? pages : [{ id: 'page_1', name: 'Page 1' }],
          pageVisuals,
        },
      }));
      setAiBuildOpen(false);
      setAiBuildPrompt('');
      toast.success(`Report "${result.title}" created with ${pages.length} pages, ${visuals.length} visuals`);
      navigate(`/dashboards/builder?id=${report.report_id}&mode=report&fromExplore=true`);
    } catch (err) { toast.error(err.message); }
    finally { setAiBuildLoading(false); }
  };


  const handleSave = async () => {
    setSaving(true);
    try {
      const report = await api.createReport({ name: formName, description: formDesc, visualization_type: formVizType });
      await api.updateReport(report.report_id, {
        name: formName, description: formDesc, visualization_type: formVizType,
        query_definition: { model_id: formModelId },
      });
      report.model_id = formModelId;
      setReports(prev => [report, ...prev]);
      setDialogOpen(false);
      toast.success('Report created');
      // Navigate to builder to configure
      navigate(`/reports/builder?id=${report.report_id}`);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null); // report_id to confirm delete

  const handleDelete = async (reportId) => {
    try {
      await api.deleteReport(reportId);
      setReports(prev => prev.filter(r => r.report_id !== reportId));
      setDeleteConfirm(null);
      toast.success('Report deleted');
    } catch (err) { toast.error(err.message); }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const folder = await api.createFolder({ name: newFolderName, parent_id: newFolderParent || null });
      setFolders(prev => [...prev, folder]);
      setNewFolderName('');
      setNewFolderParent(null);
      setNewFolderOpen(false);
      if (folder.parent_id) setExpandedFolders(prev => new Set([...prev, folder.parent_id]));
      toast.success('Folder created');
    } catch (err) { toast.error(err.message); }
  };

  const renameFolder = async (folderId) => {
    if (!renameFolderName.trim()) return;
    try {
      await api.updateFolder(folderId, { name: renameFolderName });
      setFolders(prev => prev.map(f => f.folder_id === folderId ? { ...f, name: renameFolderName } : f));
      setRenamingFolder(null);
      toast.success('Folder renamed');
    } catch (err) { toast.error(err.message); }
  };

  const deleteFolder = async (folderId) => {
    try {
      await api.deleteFolder(folderId);
      setFolders(prev => prev.filter(f => f.folder_id !== folderId));
      if (activeFolder === folderId) setActiveFolder(null);
      toast.success('Folder deleted');
    } catch (err) { toast.error(err.message); }
  };

  const moveReport = async (reportId, folderId) => {
    try {
      await api.moveReportToFolder(reportId, folderId);
      setReports(prev => prev.map(r => r.report_id === reportId ? { ...r, folder_id: folderId } : r));
      setMoveDialogOpen(false);
      setMoveReportId(null);
      toast.success('Report moved');
    } catch (err) { toast.error(err.message); }
  };

  // Drag a report card onto a folder (or "All"/"Unfiled") to move it. `folderId`
  // of null unfiles the report. Highlights the hovered folder target.
  const dropKey = (folderId) => (folderId == null ? '__null__' : folderId);
  const folderDropHandlers = (folderId, hlKey) => {
    const key = hlKey || dropKey(folderId);
    return {
      onDragOver: (e) => { if (draggedReportId) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverFolder(key); } },
      onDragLeave: () => setDragOverFolder(prev => (prev === key ? null : prev)),
      onDrop: (e) => {
        e.preventDefault();
        const rid = draggedReportId || e.dataTransfer.getData('text/plain');
        setDragOverFolder(null);
        setDraggedReportId(null);
        if (rid) moveReport(rid, folderId);
      },
    };
  };
  const cardDragProps = (reportId) => ({
    draggable: true,
    onDragStart: (e) => { setDraggedReportId(reportId); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', reportId); },
    onDragEnd: () => { setDraggedReportId(null); setDragOverFolder(null); },
  });
  const dropRing = (key) => (dragOverFolder === key ? ' ring-2 ring-primary/60 bg-primary/10' : '');

  // Build folder tree helpers
  const getChildFolderIds = useCallback((parentId) => {
    const ids = new Set();
    const collect = (pid) => {
      folders.filter(f => f.parent_id === pid).forEach(f => { ids.add(f.folder_id); collect(f.folder_id); });
    };
    collect(parentId);
    return ids;
  }, [folders]);
  const rootFolders = folders.filter(f => !f.parent_id);
  const getChildren = (parentId) => folders.filter(f => f.parent_id === parentId);
  const toggleExpand = (fid) => setExpandedFolders(prev => { const s = new Set(prev); s.has(fid) ? s.delete(fid) : s.add(fid); return s; });
  const filteredFolders = folderSearch ? folders.filter(f => f.name.toLowerCase().includes(folderSearch.toLowerCase())) : null;
  const folderReportCount = (fid) => { const childIds = getChildFolderIds(fid); return reports.filter(r => r.folder_id === fid || childIds.has(r.folder_id)).length; };

  const filteredReports = useMemo(() => {
    let list = activeFolder === null ? reports : activeFolder === 'unfiled' ? reports.filter(r => !r.folder_id) : (() => { const childIds = getChildFolderIds(activeFolder); return reports.filter(r => r.folder_id === activeFolder || childIds.has(r.folder_id)); })();
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(r => (r.name || '').toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q));
    }
    // Type filter
    if (typeFilter === 'dashboard') {
      list = list.filter(r => !(r.config?.designerMode === 'report' || r.report_type === 'pixel_perfect'));
    } else if (typeFilter === 'report') {
      list = list.filter(r => r.config?.designerMode === 'report' || r.report_type === 'pixel_perfect');
    }
    // Endorsement filter
    if (endorsementFilter !== 'all') {
      list = list.filter(r => {
        const lvl = r.endorsement?.level || 'none';
        if (endorsementFilter === 'endorsed') return lvl === 'certified' || lvl === 'promoted';
        if (endorsementFilter === 'certified') return lvl === 'certified';
        if (endorsementFilter === 'promoted') return lvl === 'promoted';
        if (endorsementFilter === 'unendorsed') return lvl === 'none';
        return true;
      });
    }
    // Stage filter (dev/test/production). Roles that can't see dev/test are
    // always clamped to production, regardless of any stale saved filter.
    const effectiveStage = canSeeNonprod ? stageFilter : 'production';
    if (effectiveStage !== 'all') {
      list = list.filter(r => (r.stage || 'dev') === effectiveStage);
    }
    // Sort
    list = [...list].sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      if (sortBy === 'oldest') return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      if (sortBy === 'name_asc') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'name_desc') return (b.name || '').localeCompare(a.name || '');
      return 0;
    });
    return list;
  }, [reports, activeFolder, searchQuery, typeFilter, endorsementFilter, stageFilter, canSeeNonprod, sortBy, folders, getChildFolderIds]);

  // Stats
  const totalDashboards = reports.filter(r => !(r.config?.designerMode === 'report' || r.report_type === 'pixel_perfect')).length;
  const totalReports = reports.filter(r => r.config?.designerMode === 'report' || r.report_type === 'pixel_perfect').length;
  const recentCount = reports.filter(r => r.created_at && (Date.now() - new Date(r.created_at).getTime()) < 7 * 24 * 60 * 60 * 1000).length;
  const certifiedCount = reports.filter(r => r.endorsement?.level === 'certified').length;
  const promotedCount = reports.filter(r => r.endorsement?.level === 'promoted').length;
  const unendorsedCount = reports.filter(r => !r.endorsement?.level || r.endorsement.level === 'none').length;

  return (
    <React.Fragment>
    <TooltipProvider>
    <div className="p-6 space-y-5" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 animate-slide-up">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight text-foreground">{getTerm('insights')}</h1>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">Interactive data visualizations and analytic insights</p>
        </div>
        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
          <ProcessHelp
            testId="reports-help"
            title="Dashboards & Reports"
            intro="Browse, build and manage your dashboards and analytic insights."
            steps={[
              'Click New to build a dashboard or report from scratch — you pick a data model, then a chart type and fields.',
              'Open any card to view it; use Compare to diff two insights and Archived to see retired ones.',
              'The AI Coach can walk you through building a chart step by step — just ask it in the assistant.',
            ]}
          />
          <Button onClick={() => setShowArchived(v => !v)} variant={showArchived ? 'default' : 'outline'} className="rounded-xl gap-1.5 h-8 text-xs" data-testid="toggle-archived-btn">
            <Archive className="w-3.5 h-3.5" /> {showArchived ? 'Viewing Archived' : 'Archived'}
          </Button>
          <Button onClick={() => navigate('/reports/compare')} variant="outline" className="rounded-xl gap-1.5 h-8 text-xs" data-testid="compare-insights-btn">
            <GitCompare className="w-3.5 h-3.5" /> Compare
          </Button>
          <Popover open={newMenuOpen} onOpenChange={setNewMenuOpen}>
            <PopoverTrigger asChild>
              <Button className="rounded-xl bg-primary text-primary-foreground transition-all active:scale-95 h-8 text-xs gap-1" data-testid="new-insight-btn" data-coach-target="reports-new-insight-build">
                <Plus className="w-3.5 h-3.5" /> New
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-1 max-h-[560px] overflow-auto" data-testid="new-insight-menu">
              <button
                onClick={() => { setNewMenuOpen(false); navigate('/dashboards/builder?new=1'); }}
                className="w-full flex items-start gap-2 px-2 py-1.5 rounded hover:bg-muted/40 text-left"
                data-testid="new-insight-build"
              >
                <Plus className="w-4 h-4 mt-0.5 text-primary" />
                <div>
                  <div className="text-xs font-medium">Build from scratch</div>
                  <div className="text-[10px] text-muted-foreground">Open the dashboard/report builder canvas.</div>
                </div>
              </button>
              <button
                onClick={() => { setNewMenuOpen(false); setAiBuildOpen(true); }}
                className="w-full flex items-start gap-2 px-2 py-1.5 rounded hover:bg-muted/40 text-left"
                data-testid="new-insight-ai-build"
              >
                <Sparkles className="w-4 h-4 mt-0.5 text-indigo-400" />
                <div>
                  <div className="text-xs font-medium">AI Build</div>
                  <div className="text-[10px] text-muted-foreground">Describe an insight in natural language.</div>
                </div>
              </button>
              <button
                onClick={() => { setNewMenuOpen(false); setImportDialogOpen(true); }}
                className="w-full flex items-start gap-2 px-2 py-1.5 rounded hover:bg-muted/40 text-left"
                data-testid="new-insight-create-from-file"
              >
                <Wand2 className="w-4 h-4 mt-0.5 text-primary" />
                <div>
                  <div className="text-xs font-medium">Create from File (AI)</div>
                  <div className="text-[10px] text-muted-foreground">Upload a PDF, Excel, or screenshot — AI builds a matching report or dashboard.</div>
                </div>
              </button>
              <button
                onClick={() => { setNewMenuOpen(false); setImportPopupOpen(true); }}
                className="w-full flex items-start gap-2 px-2 py-1.5 rounded hover:bg-muted/40 text-left"
                data-testid="new-insight-import"
              >
                <Upload className="w-4 h-4 mt-0.5 text-orange-400" />
                <div>
                  <div className="text-xs font-medium">Import</div>
                  <div className="text-[10px] text-muted-foreground">Bring in a report from BIRT, Tableau, Power BI, Qlik or Pyramid Analytics.</div>
                </div>
              </button>
              <div className="my-1 border-t border-border/25" />
              <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">Start from a template</div>
              {INSIGHT_TEMPLATES.map(t => (
                <div key={t.id} className="relative group/tpl">
                  <button
                    onClick={() => {
                      setNewMenuOpen(false);
                      // Stash the template so the builder can hydrate on mount.
                      try { window.sessionStorage.setItem('inzara.insightTemplate', JSON.stringify(t)); } catch (_) { /* ignore */ }
                      navigate(`/dashboards/builder?template=${encodeURIComponent(t.id)}`);
                    }}
                    className="w-full flex items-start gap-2 px-2 py-1.5 rounded hover:bg-muted/40 focus:bg-muted/40 focus:outline-none text-left"
                    data-testid={`new-insight-template-${t.id}`}
                  >
                    <div className="w-4 h-4 mt-0.5 rounded-sm shrink-0" style={{ background: t.color + '33', border: `1px solid ${t.color}66` }}>
                      <div className="w-full h-full rounded-sm" style={{ background: t.color + '55' }} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-medium">{t.name}</div>
                      <div className="text-[10px] text-muted-foreground line-clamp-2">{t.description}</div>
                    </div>
                  </button>
                  {/* Hover / focus preview — sits to the right of the popover,
                      pointer-events-none so it never intercepts clicks. */}
                  <div
                    className="pointer-events-none absolute left-full top-0 ml-2 z-50 opacity-0 translate-x-1 transition-all duration-150 group-hover/tpl:opacity-100 group-hover/tpl:translate-x-0 group-focus-within/tpl:opacity-100 group-focus-within/tpl:translate-x-0"
                    data-testid={`template-preview-${t.id}`}
                  >
                    <TemplateThumbnail template={t} width={180} />
                  </div>
                </div>
              ))}
            </PopoverContent>
          </Popover>
          <Button onClick={() => { setCatalogSel([]); setCatalogAi(true); setCatalogOpen(true); }} variant="outline" className="rounded-xl h-8 text-xs" data-testid="generate-catalog-btn">
            <BookOpen className="w-3.5 h-3.5 mr-1" /> Catalog
          </Button>
          <ImportReportStructureDialog
            models={models}
            onImported={handleImported}
          />
          <Button onClick={() => { if (selectMode) clearSelection(); else setSelectMode(true); }} variant={selectMode ? 'default' : 'outline'} className="rounded-xl h-8 text-xs" data-testid="bulk-select-toggle">
            <CheckSquare className="w-3.5 h-3.5 mr-1" /> {selectMode ? 'Cancel' : 'Select'}
          </Button>
          <Button onClick={() => setHistoryOpen(true)} variant="outline" className="rounded-xl h-8 text-xs" data-testid="import-history-btn">
            <History className="w-3.5 h-3.5 mr-1" /> History
          </Button>
        </div>
      </div>

      {selectMode && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-xl bg-primary/5 border border-primary/20 animate-slide-up" data-testid="bulk-action-bar">
          <span className="text-xs font-medium text-foreground" data-testid="bulk-selected-count">{selectedIds.size} selected</span>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
            <Checkbox checked={includeModelsExport} onCheckedChange={(c) => setIncludeModelsExport(!!c)} data-testid="bulk-include-models" />
            Include semantic models
          </label>
          <Button size="sm" className="h-7 text-xs" disabled={packBusy || selectedIds.size === 0} onClick={exportSelectedPack} data-testid="bulk-export-btn">
            {packBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1" />} Export pack
          </Button>
          <button className="text-xs text-primary hover:underline" onClick={() => setSelectedIds(new Set(filteredReports.map(r => r.report_id)))} data-testid="bulk-select-all">Select all</button>
          <button className="text-xs text-muted-foreground hover:underline" onClick={clearSelection} data-testid="bulk-clear">Cancel</button>
        </div>
      )}

      {/* Stats Bar */}
      <div className="flex items-center gap-3 animate-slide-up" style={{ animationDelay: '50ms' }}>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/20 border border-border/20">
          <LayoutGrid className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-medium text-foreground">{totalDashboards}</span>
          <span className="text-[10px] text-muted-foreground">Dashboards</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/20 border border-border/20">
          <FileText className="w-3.5 h-3.5 text-violet-400" />
          <span className="text-xs font-medium text-foreground">{totalReports}</span>
          <span className="text-[10px] text-muted-foreground">Reports</span>
        </div>
        {recentCount > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
            <Clock className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-xs font-medium text-emerald-400">{recentCount}</span>
            <span className="text-[10px] text-muted-foreground">this week</span>
          </div>
        )}
        {certifiedCount > 0 && (
          <button
            onClick={() => changeEndorsementFilter(endorsementFilter === 'certified' ? 'all' : 'certified')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${endorsementFilter === 'certified' ? 'bg-emerald-500/15 border border-emerald-500/40' : 'bg-emerald-500/5 border border-emerald-500/20 hover:bg-emerald-500/10'}`}
            title="Click to toggle Certified-only view"
            data-testid="stat-certified"
          >
            <BadgeCheck className="w-3.5 h-3.5 text-emerald-400" strokeWidth={2.5} />
            <span className="text-xs font-medium text-emerald-400">{certifiedCount}</span>
            <span className="text-[10px] text-muted-foreground">Certified</span>
          </button>
        )}
        {promotedCount > 0 && (
          <button
            onClick={() => changeEndorsementFilter(endorsementFilter === 'endorsed' ? 'all' : 'endorsed')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${endorsementFilter === 'endorsed' ? 'bg-amber-500/15 border border-amber-500/40' : 'bg-amber-500/5 border border-amber-500/20 hover:bg-amber-500/10'}`}
            title="Click to toggle Endorsed-only view (Certified + Promoted)"
            data-testid="stat-promoted"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-amber-400" strokeWidth={2.5} />
            <span className="text-xs font-medium text-amber-400">{promotedCount}</span>
            <span className="text-[10px] text-muted-foreground">Promoted</span>
          </button>
        )}
      </div>

      {/* Search & Filter Bar */}
      <div className="flex items-center gap-3 animate-slide-up" style={{ animationDelay: '100ms' }}>
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={`Search ${getTerm('insights', 'plural', 'lower')} by name or description...`}
            className="w-full pl-10 pr-4 py-2 rounded-xl text-sm bg-muted/20 border border-border/30 outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20 transition-all text-foreground placeholder:text-muted-foreground/70"
            data-testid="insights-search"
          />
        </div>
        {/* Type Filter Chips */}
        <div className="flex items-center gap-1 border border-border/30 rounded-xl p-0.5" data-testid="type-filter">
          {[
            { key: 'all', label: 'All', icon: LayoutGrid },
            { key: 'dashboard', label: 'Dashboards', icon: BarChart3 },
            { key: 'report', label: 'Reports', icon: FileText },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setTypeFilter(f.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${typeFilter === f.key ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}`}
              data-testid={`filter-${f.key}`}
            >
              <f.icon className="w-3 h-3" />{f.label}
            </button>
          ))}
        </div>
        {/* Endorsement Filter Chips — surface single-source-of-truth content */}
        <div className="flex items-center gap-1 border border-border/30 rounded-xl p-0.5" data-testid="endorsement-filter">
          {[
            { key: 'all',        label: 'Any',        icon: null,          cls: 'text-muted-foreground' },
            { key: 'endorsed',   label: 'Endorsed',   icon: ShieldCheck,   cls: 'text-amber-300' },
            { key: 'certified',  label: 'Certified',  icon: BadgeCheck,    cls: 'text-emerald-300' },
            ...(isAdmin ? [{ key: 'unendorsed', label: 'Unendorsed', icon: ShieldOff, cls: 'text-muted-foreground' }] : []),
          ].map(f => {
            const active = endorsementFilter === f.key;
            const Icon = f.icon;
            return (
              <Tooltip key={f.key}><TooltipTrigger asChild>
                <button
                  onClick={() => changeEndorsementFilter(f.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${active ? 'bg-primary/15 text-primary' : `${f.cls} hover:text-foreground hover:bg-muted/20`}`}
                  data-testid={`endorsement-filter-${f.key}`}
                >
                  {Icon && <Icon className="w-3 h-3" />}{f.label}
                </button>
              </TooltipTrigger><TooltipContent>
                <p className="text-xs">
                  {f.key === 'all' && 'Show every dashboard & report'}
                  {f.key === 'endorsed' && `Show Certified + Promoted (${certifiedCount + promotedCount})`}
                  {f.key === 'certified' && `Show only Certified (${certifiedCount})`}
                  {f.key === 'unendorsed' && `Admin view: items missing endorsement (${unendorsedCount})`}
                </p>
              </TooltipContent></Tooltip>
            );
          })}
        </div>
        {/* Stage filter — only for roles that can see dev/test */}
        {canSeeNonprod && (
          <Select value={stageFilter} onValueChange={changeStageFilter}>
            <SelectTrigger className="w-[150px] rounded-xl bg-muted/20 border-border/30 text-xs h-9" data-testid="stage-filter-select">
              <Filter className="w-3 h-3 mr-1.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              <SelectItem value="production">Production only</SelectItem>
              <SelectItem value="test">Test only</SelectItem>
              <SelectItem value="dev">Dev only</SelectItem>
            </SelectContent>
          </Select>
        )}
        {/* Sort Dropdown */}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[140px] rounded-xl bg-muted/20 border-border/30 text-xs h-9" data-testid="sort-select">
            <ArrowUpDown className="w-3 h-3 mr-1.5 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="name_asc">Name A-Z</SelectItem>
            <SelectItem value="name_desc">Name Z-A</SelectItem>
          </SelectContent>
        </Select>
        {/* View toggle */}
        <div className="flex items-center border border-border/30 rounded-xl overflow-hidden">
          <Tooltip><TooltipTrigger asChild>
            <button onClick={() => changeViewMode('cards')} className={`p-2 transition-colors ${viewMode === 'cards' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="rpt-view-cards"><LayoutGrid className="w-4 h-4" /></button>
          </TooltipTrigger><TooltipContent><p className="text-xs">Card view</p></TooltipContent></Tooltip>
          <Tooltip><TooltipTrigger asChild>
            <button onClick={() => changeViewMode('table')} className={`p-2 transition-colors ${viewMode === 'table' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="rpt-view-table"><List className="w-4 h-4" /></button>
          </TooltipTrigger><TooltipContent><p className="text-xs">List view</p></TooltipContent></Tooltip>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
        {/* Folder Sidebar */}
        <div className="w-52 flex-shrink-0 space-y-1" data-testid="folder-sidebar">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Folders</p>
            <button onClick={() => { setNewFolderOpen(true); setNewFolderParent(null); }} className="text-muted-foreground hover:text-primary transition-colors" title="New folder" data-testid="create-folder-btn"><FolderPlus className="w-3.5 h-3.5" /></button>
          </div>
          {/* Search */}
          <div className="relative mb-1.5">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground/70" />
            <input value={folderSearch} onChange={e => setFolderSearch(e.target.value)} placeholder="Search folders..." className="w-full pl-7 pr-2 py-1 rounded-lg text-[10px] bg-muted/20 border border-border/30 outline-none focus:border-primary/30" data-testid="folder-search" />
          </div>
          <button onClick={() => setActiveFolder(null)} {...folderDropHandlers(null, '__all__')} className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs flex items-center gap-2 transition-colors ${activeFolder === null ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}${dropRing('__all__')}`} data-testid="folder-all">
            <LayoutGrid className="w-3.5 h-3.5" /> All {getTerm('insights')} <span className="ml-auto text-[10px] text-muted-foreground/80">{reports.length}</span>
          </button>
          <button onClick={() => setActiveFolder('unfiled')} {...folderDropHandlers(null)} className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs flex items-center gap-2 transition-colors ${activeFolder === 'unfiled' ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}${dropRing('unfiled')}`} data-testid="folder-unfiled">
            <FileText className="w-3.5 h-3.5" /> Unfiled <span className="ml-auto text-[10px] text-muted-foreground/80">{reports.filter(r => !r.folder_id).length}</span>
          </button>
          {/* Folder Tree */}
          {(filteredFolders || rootFolders).map(folder => {
            const children = getChildren(folder.folder_id);
            const isExpanded = expandedFolders.has(folder.folder_id);
            const hasChildren = children.length > 0;
            return (
              <div key={folder.folder_id}>
                <div className="group/folder flex items-center">
                  {renamingFolder === folder.folder_id ? (
                    <input value={renameFolderName} onChange={e => setRenameFolderName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') renameFolder(folder.folder_id); if (e.key === 'Escape') setRenamingFolder(null); }} onBlur={() => { if (renameFolderName.trim() && renameFolderName !== folder.name) renameFolder(folder.folder_id); else setRenamingFolder(null); }} autoFocus className="flex-1 px-2.5 py-1 rounded-lg text-xs bg-muted/30 border border-primary/30 outline-none text-foreground" />
                  ) : (
                    <button onClick={() => setActiveFolder(folder.folder_id)} {...folderDropHandlers(folder.folder_id)} className={`flex-1 text-left px-2.5 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-colors ${activeFolder === folder.folder_id ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}${dropRing(folder.folder_id)}`} data-testid={`folder-${folder.folder_id}`}>
                      {hasChildren && <span onClick={e => { e.stopPropagation(); toggleExpand(folder.folder_id); }} className="flex-shrink-0 cursor-pointer"><ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} /></span>}
                      {!hasChildren && <span className="w-3" />}
                      {activeFolder === folder.folder_id ? <FolderOpen className="w-3.5 h-3.5 flex-shrink-0" /> : <FolderClosed className="w-3.5 h-3.5 flex-shrink-0" />}
                      <span className="truncate">{folder.name}</span>
                      <span className="ml-auto text-[10px] text-muted-foreground/80 flex-shrink-0">{folderReportCount(folder.folder_id)}</span>
                    </button>
                  )}
                  <div className="opacity-0 group-hover/folder:opacity-100 flex items-center ml-0.5 flex-shrink-0">
                    <button onClick={e => { e.stopPropagation(); setNewFolderOpen(true); setNewFolderParent(folder.folder_id); }} className="p-0.5 text-muted-foreground/70 hover:text-primary" title="Add subfolder"><FolderPlus className="w-2.5 h-2.5" /></button>
                    <button onClick={e => { e.stopPropagation(); setPermFolderId(folder.folder_id); setPermDialogOpen(true); }} className="p-0.5 text-muted-foreground/70 hover:text-primary" title="Permissions"><Shield className="w-2.5 h-2.5" /></button>
                    <button onClick={e => { e.stopPropagation(); setRenamingFolder(folder.folder_id); setRenameFolderName(folder.name); }} className="p-0.5 text-muted-foreground/70 hover:text-foreground" title="Rename"><Pencil className="w-2.5 h-2.5" /></button>
                    <button onClick={e => { e.stopPropagation(); deleteFolder(folder.folder_id); }} className="p-0.5 text-muted-foreground/70 hover:text-destructive" title="Delete"><Trash2 className="w-2.5 h-2.5" /></button>
                  </div>
                </div>
                {/* Children (nested) */}
                {isExpanded && !filteredFolders && children.map(child => {
                  const grandchildren = getChildren(child.folder_id);
                  const childExpanded = expandedFolders.has(child.folder_id);
                  return (
                    <div key={child.folder_id} className="pl-4">
                      <div className="group/folder flex items-center">
                        <button onClick={() => setActiveFolder(child.folder_id)} {...folderDropHandlers(child.folder_id)} className={`flex-1 text-left px-2 py-1 rounded-lg text-[11px] flex items-center gap-1.5 transition-colors ${activeFolder === child.folder_id ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}${dropRing(child.folder_id)}`} data-testid={`folder-${child.folder_id}`}>
                          {grandchildren.length > 0 && <button onClick={e => { e.stopPropagation(); toggleExpand(child.folder_id); }} className="flex-shrink-0"><ChevronRight className={`w-2.5 h-2.5 transition-transform ${childExpanded ? 'rotate-90' : ''}`} /></button>}
                          {grandchildren.length === 0 && <span className="w-2.5" />}
                          <FolderClosed className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{child.name}</span>
                          <span className="ml-auto text-[9px] text-muted-foreground/80 flex-shrink-0">{folderReportCount(child.folder_id)}</span>
                        </button>
                        <div className="opacity-0 group-hover/folder:opacity-100 flex items-center ml-0.5 flex-shrink-0">
                          <button onClick={e => { e.stopPropagation(); setRenamingFolder(child.folder_id); setRenameFolderName(child.name); }} className="p-0.5 text-muted-foreground/70 hover:text-foreground"><Pencil className="w-2 h-2" /></button>
                          <button onClick={e => { e.stopPropagation(); deleteFolder(child.folder_id); }} className="p-0.5 text-muted-foreground/70 hover:text-destructive"><Trash2 className="w-2 h-2" /></button>
                        </div>
                      </div>
                      {/* Grandchildren */}
                      {childExpanded && grandchildren.map(gc => (
                        <div key={gc.folder_id} className="pl-4">
                          <button onClick={() => setActiveFolder(gc.folder_id)} {...folderDropHandlers(gc.folder_id)} className={`w-full text-left px-2 py-1 rounded-lg text-[10px] flex items-center gap-1.5 transition-colors ${activeFolder === gc.folder_id ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}${dropRing(gc.folder_id)}`}>
                            <FolderClosed className="w-2.5 h-2.5 flex-shrink-0" />
                            <span className="truncate">{gc.name}</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })}
                {/* Inline subfolder input — appears nested under this folder */}
                {newFolderOpen && newFolderParent === folder.folder_id && (
                  <div className="pl-4 flex items-center gap-1 mt-0.5" data-testid="new-subfolder-inline">
                    <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') { setNewFolderOpen(false); setNewFolderName(''); setNewFolderParent(null); } }} onBlur={() => { if (!newFolderName.trim()) { setNewFolderOpen(false); setNewFolderParent(null); } }} autoFocus placeholder="Subfolder name" className="flex-1 px-2 py-1 rounded-lg text-[11px] bg-muted/30 border border-primary/30 outline-none" data-testid="new-folder-input" />
                    <button onClick={createFolder} className="text-primary text-[11px] font-medium">Add</button>
                  </div>
                )}
              </div>
            );
          })}
          {newFolderOpen && !newFolderParent && (
            <div className="flex items-center gap-1">
              <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') { setNewFolderOpen(false); setNewFolderName(''); setNewFolderParent(null); } }} autoFocus placeholder="Folder name" className="flex-1 px-2.5 py-1 rounded-lg text-xs bg-muted/30 border border-primary/30 outline-none" data-testid="new-folder-input" />
              <button onClick={createFolder} className="text-primary text-xs font-medium">Add</button>
            </div>
          )}
        </div>

        {/* Reports Content */}
        <div className="flex-1 min-w-0">

      {/* Card View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger-children" data-testid="reports-grid">
          {filteredReports.map(report => {
            const VizIcon = vizIcons[report.visualization_type] || FileText;
            const isReport = report.config?.designerMode === 'report' || report.report_type === 'pixel_perfect';
            const vizCount = (report.config?.visuals || []).length;
            const stageMeta = ({
              production: { label: 'Production', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
              test: { label: 'Test', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
              dev: { label: 'Development', cls: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
            })[report.stage || 'dev'];
            return (
              <div key={report.report_id} {...cardDragProps(report.report_id)} onClick={() => { if (selectMode) { toggleSelect(report.report_id); } else { navigate(`/reports/view?id=${report.report_id}`); } }} className={`glass-card relative rounded-2xl overflow-hidden hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 cursor-pointer group${draggedReportId === report.report_id ? ' opacity-50' : ''}${selectMode && selectedIds.has(report.report_id) ? ' ring-2 ring-primary' : ''}`} data-testid={`report-card-${report.report_id}`}>
                {selectMode && (
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20" onClick={(e) => { e.stopPropagation(); toggleSelect(report.report_id); }}>
                    <div className="bg-background/90 rounded-md p-0.5 shadow">
                      <Checkbox checked={selectedIds.has(report.report_id)} data-testid={`select-report-${report.report_id}`} />
                    </div>
                  </div>
                )}
                {/* Thumbnail / Preview Area */}
                <div className={`h-32 relative flex items-center justify-center ${isReport ? 'bg-gradient-to-br from-violet-500/10 via-violet-500/5 to-transparent' : 'bg-gradient-to-br from-primary/10 via-primary/5 to-transparent'}`}>
                  {report.thumbnail ? (
                    <img src={report.thumbnail} alt="" className="w-full h-full object-cover object-top" loading="lazy" />
                  ) : (
                    <div className="flex flex-col items-center gap-1.5">
                      <VizIcon className={`w-8 h-8 ${isReport ? 'text-violet-400/40' : 'text-primary/40'}`} strokeWidth={1} />
                      {vizCount > 0 && <span className="text-[9px] text-muted-foreground/70">{vizCount} visual{vizCount !== 1 ? 's' : ''}</span>}
                      <span className="text-[8px] text-muted-foreground/25 flex items-center gap-1"><Camera className="w-2.5 h-2.5" />Preview on first view</span>
                    </div>
                  )}
                  {/* Quick View Overlay */}
                  <div className="absolute inset-0 bg-background/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-2">
                    <button className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-[10px] font-medium shadow-md hover:shadow-lg transition-all" data-testid={`view-report-${report.report_id}`}>
                      <Eye className="w-3 h-3 inline mr-1" />View
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); openEdit(report); }} className="px-3 py-1.5 rounded-lg bg-muted/80 text-foreground text-[10px] font-medium shadow-md hover:shadow-lg transition-all" data-testid={`edit-report-${report.report_id}`}>
                      <Pencil className="w-3 h-3 inline mr-1" />Edit
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); toast.info('Refreshing thumbnail…'); navigate(`/reports/view?id=${report.report_id}&refreshThumb=1`); }} title="Refresh thumbnail" className="px-3 py-1.5 rounded-lg bg-muted/80 text-foreground text-[10px] font-medium shadow-md hover:shadow-lg transition-all" data-testid={`refresh-thumbnail-${report.report_id}`}>
                      <RefreshCw className="w-3 h-3 inline mr-1" />Thumbnail
                    </button>
                    {report.archived && (
                      <button onClick={(e) => { e.stopPropagation(); unarchiveReport(report); }} title="Restore to Insights list" className="px-3 py-1.5 rounded-lg bg-emerald-500/80 text-white text-[10px] font-medium shadow-md hover:shadow-lg transition-all" data-testid={`unarchive-report-${report.report_id}`}>
                        <Archive className="w-3 h-3 inline mr-1" />Restore
                      </button>
                    )}
                  </div>
                  {/* Type Badge */}
                  <Badge className={`absolute top-2 right-2 text-[8px] ${isReport ? 'bg-violet-500/20 text-violet-400 border-violet-500/30' : 'bg-primary/20 text-primary border-primary/30'}`}>
                    {isReport ? 'Report' : 'Dashboard'}
                  </Badge>
                  {/* Promotion stage badge */}
                  <Badge className={`absolute top-2 left-2 text-[8px] ${stageMeta.cls}`} data-testid={`report-stage-${report.report_id}`}>
                    {stageMeta.label}
                  </Badge>
                </div>
                {/* Card Body */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 title={report.name} className="text-sm font-heading font-semibold text-foreground flex-1 break-words">{report.name}{report._version != null && <span className="ml-1.5 font-mono font-normal text-xs text-muted-foreground" data-testid={`report-card-version-${report.report_id}`}>v{report._version}</span>}</h3>
                    {/* Endorsement badge — visible to everyone; only admins
                        get the click-to-change menu. */}
                    <span onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                      <EndorsementMenu
                        contentType={report.visualization_type === 'dashboard' ? 'dashboard' : 'report'}
                        contentId={report.report_id}
                        endorsement={report.endorsement}
                        onChange={(end) => setReports(prev => prev.map(r => r.report_id === report.report_id ? { ...r, endorsement: end } : r))}
                        compact
                      />
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground line-clamp-2 mb-3 min-h-[2rem]">{report.description || 'No description'}</p>
                  {report.import_source === 'pyramid' && report.import_meta?.match_health && (() => {
                    const mh = report.import_meta.match_health;
                    const needs = (mh.unmatched || 0) > 0;
                    return (
                      <Badge className={`mb-2 text-[8px] gap-1 ${needs ? 'bg-amber-500/15 text-amber-600 dark:text-amber-300 border-amber-500/30' : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30'}`} data-testid={`match-health-${report.report_id}`}>
                        {needs ? <><AlertTriangle className="w-2.5 h-2.5" /> Needs attention: {mh.unmatched} field{mh.unmatched !== 1 ? 's' : ''}</> : <><CheckCircle2 className="w-2.5 h-2.5" /> Fully mapped</>}
                      </Badge>
                    );
                  })()}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-0.5 text-[10px] text-muted-foreground/80 min-w-0">
                      <div className="flex items-center gap-1.5 truncate">
                        <Calendar className="w-3 h-3 shrink-0" />
                        <span className="truncate">{report.created_at ? new Date(report.created_at).toLocaleDateString() : 'N/A'}{(userMap[report.created_by] || report.created_by) ? ` · ${userMap[report.created_by] || report.created_by}` : ''}</span>
                      </div>
                      {(report.updated_at || report.last_modified) && (
                        <div className="pl-[18px] truncate" data-testid={`report-card-modified-${report.report_id}`}>Modified {new Date(report.updated_at || report.last_modified).toLocaleDateString()}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all">
                      <Tooltip><TooltipTrigger asChild>
                        <button onClick={(e) => { e.stopPropagation(); setEmbedReport(report); setEmbedOpen(true); }} className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary" data-testid={`embed-report-${report.report_id}`}><Link2 className="w-3 h-3" /></button>
                      </TooltipTrigger><TooltipContent><p className="text-xs">Share</p></TooltipContent></Tooltip>
                      <Tooltip><TooltipTrigger asChild>
                        <button onClick={(e) => { e.stopPropagation(); downloadDocument(report); }} disabled={docLoading === report.report_id} className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary" data-testid={`document-report-${report.report_id}`}>
                          {docLoading === report.report_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <BookOpen className="w-3 h-3" />}
                        </button>
                      </TooltipTrigger><TooltipContent><p className="text-xs">Document (PDF)</p></TooltipContent></Tooltip>
                      <Tooltip><TooltipTrigger asChild>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button onClick={(e) => e.stopPropagation()} disabled={exportingId === report.report_id} className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary" data-testid={`export-report-${report.report_id}`}>
                              {exportingId === report.report_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportReportStructure(report, false); }} data-testid={`export-report-structure-${report.report_id}`}>Structure only</DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); exportReportStructure(report, true); }} data-testid={`export-report-withmodel-${report.report_id}`}>Include semantic model</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TooltipTrigger><TooltipContent><p className="text-xs">Export (.inzara-report.json)</p></TooltipContent></Tooltip>
                      <Tooltip><TooltipTrigger asChild>
                        <button onClick={(e) => { e.stopPropagation(); setMoveReportId(report.report_id); setMoveDialogOpen(true); }} className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary"><FolderOpen className="w-3 h-3" /></button>
                      </TooltipTrigger><TooltipContent><p className="text-xs">Move</p></TooltipContent></Tooltip>
                      <span onClick={e => e.stopPropagation()}><VersionHistoryButton resourceType="report" resourceId={report.report_id} resourceName={report.name} /></span>
                      {report.import_source === 'pyramid' && (
                        <Tooltip><TooltipTrigger asChild>
                          <button onClick={(e) => { e.stopPropagation(); resyncPyramid(report); }} disabled={resyncingId === report.report_id} className="p-1 rounded hover:bg-amber-500/10 text-muted-foreground hover:text-amber-400" data-testid={`resync-pyramid-${report.report_id}`}>
                            {resyncingId === report.report_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                          </button>
                        </TooltipTrigger><TooltipContent><p className="text-xs">Re-sync from Pyramid</p></TooltipContent></Tooltip>
                      )}
                      <Tooltip><TooltipTrigger asChild>
                        <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(report.report_id); }} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" data-testid={`delete-report-${report.report_id}`}><Trash2 className="w-3 h-3" /></button>
                      </TooltipTrigger><TooltipContent><p className="text-xs">Delete</p></TooltipContent></Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {filteredReports.length === 0 && !loading && (
            <div className="col-span-full glass-card rounded-2xl p-12 text-center">
              {searchQuery || typeFilter !== 'all' || endorsementFilter !== 'all' ? (
                <>
                  <Search className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm font-heading text-foreground mb-1">No matching {getTerm('insights', 'plural', 'lower')}</p>
                  <p className="text-xs text-muted-foreground">Try adjusting your search or filters</p>
                  <button onClick={() => { setSearchQuery(''); setTypeFilter('all'); changeEndorsementFilter('all'); }} className="mt-2 text-xs text-primary hover:underline">Clear filters</button>
                </>
              ) : (
                <>
                  <FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-sm font-heading text-foreground mb-1">No {getTerm('insights', 'plural', 'lower')} yet</p>
                  <p className="text-xs text-muted-foreground">Create a dashboard or report to get started</p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="glass-card rounded-2xl overflow-hidden" data-testid="reports-table">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/50">
                <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Created By</th>
                <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Last Modified</th>
                <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.map((report, idx) => {
                const VizIcon = vizIcons[report.visualization_type] || FileText;
                return (
                  <tr key={report.report_id} {...cardDragProps(report.report_id)} className={'border-b border-border/20 hover:bg-muted/40 transition-colors group ' + (idx % 2 === 1 ? 'bg-muted/30' : '') + (draggedReportId === report.report_id ? ' opacity-50' : '')} data-testid={`report-row-${report.report_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <VizIcon className="w-4 h-4 text-primary/60" strokeWidth={1.5} />
                        <span className="font-medium text-primary hover:underline cursor-pointer" onClick={() => navigate(`/reports/view?id=${report.report_id}`)}>{report.name}</span>
                        {report._version != null && <span className="font-mono text-xs text-muted-foreground" data-testid={`report-row-version-${report.report_id}`}>v{report._version}</span>}
                        <EndorsementBadge endorsement={report.endorsement} size="sm" showLabel={false} />
                      </div>
                    </td>
                    <td className="px-4 py-3"><Badge variant="outline" className={`text-[8px] ${report.config?.designerMode === 'report' || report.report_type === 'pixel_perfect' ? 'border-violet-500/30 text-violet-400' : 'border-primary/30 text-primary'}`}>{report.config?.designerMode === 'report' || report.report_type === 'pixel_perfect' ? 'Report' : 'Dashboard'}</Badge></td>
                    <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">{report.description}</td>
                    <td className="px-4 py-3 text-muted-foreground">{report.created_at ? new Date(report.created_at).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3 text-muted-foreground truncate max-w-[12rem]" data-testid={`report-creator-${report.report_id}`}>{userMap[report.created_by] || report.created_by || '-'}</td>
                    <td className="px-4 py-3 text-muted-foreground" data-testid={`report-modified-${report.report_id}`}>{(report.updated_at || report.last_modified || report.created_at) ? new Date(report.updated_at || report.last_modified || report.created_at).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3">{report.lifecycle_state ? <Badge variant="outline" className="text-[9px]">{report.lifecycle_state}</Badge> : <Badge variant="outline" className="text-[9px] text-muted-foreground/70">-</Badge>}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Tooltip><TooltipTrigger asChild>
                          <button onClick={() => openEdit(report)} className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground" data-testid={`edit-report-row-${report.report_id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        </TooltipTrigger><TooltipContent><p className="text-xs">Edit in builder</p></TooltipContent></Tooltip>
                        <Tooltip><TooltipTrigger asChild>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button disabled={exportingId === report.report_id} className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground" data-testid={`export-report-row-${report.report_id}`}>
                                {exportingId === report.report_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => exportReportStructure(report, false)} data-testid={`export-report-row-structure-${report.report_id}`}>Structure only</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => exportReportStructure(report, true)} data-testid={`export-report-row-withmodel-${report.report_id}`}>Include semantic model</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TooltipTrigger><TooltipContent><p className="text-xs">Export (.inzara-report.json)</p></TooltipContent></Tooltip>
                        <Tooltip><TooltipTrigger asChild>
                          <button onClick={() => setDeleteConfirm(report.report_id)} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" data-testid={`delete-report-row-${report.report_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </TooltipTrigger><TooltipContent><p className="text-xs">Delete</p></TooltipContent></Tooltip>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {reports.length === 0 && !loading && (
            <div className="p-12 text-center">
              {searchQuery || typeFilter !== 'all' || endorsementFilter !== 'all' ? (
                <>
                  <p className="text-xs text-muted-foreground mb-1">No matching {getTerm('insights', 'plural', 'lower')} found</p>
                  <button onClick={() => { setSearchQuery(''); setTypeFilter('all'); changeEndorsementFilter('all'); }} className="text-xs text-primary hover:underline">Clear filters</button>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">No reports yet</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Create Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Semantic Model</Label>
              <Select value={formModelId} onValueChange={setFormModelId}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50" data-testid="report-model-select"><SelectValue placeholder="Select a model" /></SelectTrigger>
                <SelectContent>
                  {models.map(m => <SelectItem key={m.model_id} value={m.model_id} className="text-xs">{m.name} <span className="text-muted-foreground/70 ml-1">({m.status})</span></SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[9px] text-muted-foreground/80">The report will use this model's dimensions and measures</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Report Name</Label>
              <Input data-testid="report-name-input" value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g. Q1 Loss Ratio Analysis" className="rounded-lg bg-muted/50 border-border/50" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Input value={formDesc} onChange={e => setFormDesc(e.target.value)} placeholder="Brief description" className="rounded-lg bg-muted/50 border-border/50" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Visualization Type</Label>
              <Select value={formVizType} onValueChange={setFormVizType}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="table">Table</SelectItem>
                  <SelectItem value="bar_chart">Bar Chart</SelectItem>
                  <SelectItem value="line_chart">Line Chart</SelectItem>
                  <SelectItem value="pie_chart">Pie Chart</SelectItem>
                  <SelectItem value="area_chart">Area Chart</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSave} disabled={saving || !formName.trim() || !formModelId} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="save-report-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create & Open in Builder
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ImportHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} />


      {/* Move to Folder Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={v => { setMoveDialogOpen(v); if (!v) setMoveReportId(null); }}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-xs" data-testid="move-to-folder-dialog">
          <DialogHeader><DialogTitle className="font-heading text-foreground">Move to Folder</DialogTitle></DialogHeader>
          <div className="space-y-1 mt-2">
            <button onClick={() => moveReport(moveReportId, null)} className="w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-muted/30 transition-colors flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <FileText className="w-3.5 h-3.5" /> Unfiled (Root)
            </button>
            {folders.map(f => (
              <button key={f.folder_id} onClick={() => moveReport(moveReportId, f.folder_id)} className="w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-muted/30 transition-colors flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid={`move-to-${f.folder_id}`}>
                <FolderClosed className="w-3.5 h-3.5 text-primary" /> {f.name}
              </button>
            ))}
            {folders.length === 0 && <p className="text-xs text-muted-foreground/70 text-center py-4">No folders yet. Create one first.</p>}
          </div>
        </DialogContent>
      </Dialog>
      </div>
      </div>
    </div>
    </TooltipProvider>
    {embedReport && <EmbedDialog open={embedOpen} onOpenChange={setEmbedOpen} resourceType="report" resourceId={embedReport.report_id} resourceName={embedReport.name} />}
    {/* Delete Confirmation Dialog */}
    <Dialog open={!!deleteConfirm} onOpenChange={v => { if (!v) setDeleteConfirm(null); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm" data-testid="delete-confirm-dialog">
        <DialogHeader><DialogTitle className="font-heading text-foreground">Delete Dashboard?</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <p className="text-sm text-muted-foreground">This action cannot be undone. The dashboard and all its visuals will be permanently deleted.</p>
          {deleteConfirm && (() => {
            // Reports listing covers both dashboards and pixel-perfect reports.
            // Pick the right asset kind for the impact lookup.
            const target = reports.find(r => r.report_id === deleteConfirm);
            const kind = (target?.config?.designerMode === 'report' || target?.report_type === 'pixel_perfect') ? 'report' : 'dashboard';
            return <ImpactWarning kind={kind} id={deleteConfirm} />;
          })()}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1 rounded-lg">Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} className="flex-1 rounded-lg" data-testid="confirm-delete-btn">Delete</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    {/* Folder Permissions Dialog */}
    <Dialog open={permDialogOpen} onOpenChange={setPermDialogOpen}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="permissions-dialog">
        <DialogHeader><DialogTitle className="font-heading text-foreground flex items-center gap-2"><Shield className="w-4 h-4 text-primary" /> Folder Permissions</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <p className="text-xs text-muted-foreground">Manage access to "{folders.find(f => f.folder_id === permFolderId)?.name}" and its reports.</p>
          {['viewer', 'editor', 'admin'].map(role => {
            const folder = folders.find(f => f.folder_id === permFolderId);
            const perms = folder?.permissions || [];
            const roleUsers = perms.filter(p => p.role === role);
            return (
              <div key={role} className="p-3 rounded-lg border border-border/30">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-foreground capitalize">{role}</span>
                  <span className="text-[9px] text-muted-foreground">{role === 'viewer' ? 'View only' : role === 'editor' ? 'View + Edit' : 'Full control'}</span>
                </div>
                {roleUsers.length > 0 && (
                  <div className="space-y-0.5 mb-1.5">{roleUsers.map((p) => (
                    <div key={`${p.user_id}-${role}`} className="flex items-center justify-between text-[10px] text-foreground/70 px-1.5 py-0.5 bg-muted/10 rounded">
                      <span title={p.user_id}>{userMap[p.user_id] || p.user_id}</span>
                      <button onClick={async () => {
                        const newPerms = perms.filter(pp => !(pp.user_id === p.user_id && pp.role === role));
                        try { await api.updateFolderPermissions(permFolderId, newPerms); setFolders(prev => prev.map(f => f.folder_id === permFolderId ? { ...f, permissions: newPerms } : f)); toast.success('Permission removed'); } catch (err) { toast.error(err.message); }
                      }} className="text-muted-foreground/70 hover:text-destructive"><Trash2 className="w-2.5 h-2.5" /></button>
                    </div>
                  ))}</div>
                )}
                <div className="flex gap-1">
                  <Input id={`perm-input-${role}`} placeholder="User ID or email" className="h-6 text-[9px] bg-muted/20 border-border/20 flex-1 text-foreground" onKeyDown={async (e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      const userId = e.target.value.trim();
                      const newPerms = [...perms, { user_id: userId, role }];
                      try { await api.updateFolderPermissions(permFolderId, newPerms); setFolders(prev => prev.map(f => f.folder_id === permFolderId ? { ...f, permissions: newPerms } : f)); e.target.value = ''; toast.success(`${role} permission added`); } catch (err) { toast.error(err.message); }
                    }
                  }} />
                  <Button size="sm" variant="outline" className="h-6 text-[8px] px-2" onClick={async () => {
                    const input = document.getElementById(`perm-input-${role}`);
                    if (!input?.value?.trim()) return;
                    const userId = input.value.trim();
                    const newPerms = [...perms, { user_id: userId, role }];
                    try { await api.updateFolderPermissions(permFolderId, newPerms); setFolders(prev => prev.map(f => f.folder_id === permFolderId ? { ...f, permissions: newPerms } : f)); input.value = ''; toast.success(`${role} permission added`); } catch (err) { toast.error(err.message); }
                  }}>Add</Button>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
    
    {/* Import Report Dialog */}
    <ImportReportDialog 
      open={importDialogOpen} 
      onOpenChange={setImportDialogOpen}
    />

    {/* Pyramid Analytics Import */}
    <PyramidImportDialog
      open={pyramidImportOpen}
      onOpenChange={setPyramidImportOpen}
      models={models}
    />

    {/* AI Build Dashboard/Report Dialog */}
    <Dialog open={aiBuildOpen} onOpenChange={(v) => { if (!v) resetAiBuild(); else setAiBuildOpen(true); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-400" /> AI Build {aiBuildMode === 'report' ? 'Report' : 'Dashboard'}
            {aiBuildMode === 'dashboard' && aiBuildStep === 'review' && (
              <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-indigo-700 dark:text-indigo-300/80 bg-indigo-500/10 border border-indigo-500/20 px-2 py-0.5 rounded-full">Step 2 of 2 · Review plan</span>
            )}
            {aiBuildMode === 'dashboard' && aiBuildStep === 'compose' && (
              <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/70">Step 1 of 2</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* ── STEP 1 / Single-step report mode ── */}
        {(aiBuildMode === 'report' || aiBuildStep === 'compose') && (
          <div className="space-y-4 mt-2">
            {/* Mode toggle */}
            <div className="flex gap-1 p-0.5 rounded-lg bg-muted/30">
              <button onClick={() => { setAiBuildMode('dashboard'); }} className={`flex-1 text-xs py-1.5 rounded-md transition-colors ${aiBuildMode === 'dashboard' ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/20'}`}>Dashboard</button>
              <button onClick={() => { setAiBuildMode('report'); setAiBuildStep('compose'); setAiBuildPlan(null); }} className={`flex-1 text-xs py-1.5 rounded-md transition-colors ${aiBuildMode === 'report' ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/20'}`}>Pixel-Perfect Report</button>
            </div>
            <div>
              <Label className="text-sm">Semantic Model</Label>
              <Select value={aiBuildModelId} onValueChange={setAiBuildModelId}>
                <SelectTrigger className="mt-1" data-testid="ai-build-model-select">
                  <SelectValue placeholder="Select model..." />
                </SelectTrigger>
                <SelectContent>
                  {models.map(m => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Describe the {aiBuildMode === 'report' ? 'report' : 'dashboard'} you want</Label>
              <textarea
                value={aiBuildPrompt}
                onChange={e => setAiBuildPrompt(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { aiBuildMode === 'report' ? handleAiBuildReport() : handleGeneratePlan(); } }}
                placeholder={aiBuildMode === 'report'
                  ? 'e.g. Generate a quarterly board pack with executive summary, premium analysis by LOB, claims trends, loss triangle, and a detail appendix'
                  : 'e.g. Create an executive dashboard showing premium trends, loss ratios by LOB, top claims, and key KPIs'}
                className="w-full mt-1 rounded-lg border border-border/50 bg-muted/30 p-3 text-sm min-h-[100px] resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500/30"
                data-testid="ai-build-prompt"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                {aiBuildMode === 'report' ? 'Cmd+Enter to build' : 'Cmd+Enter to generate plan — review intents before building'}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {(aiBuildMode === 'report' ? [
                'Quarterly board pack with executive summary and financials',
                'Loss run report grouped by LOB with subtotals',
                'Premium analysis report with year-over-year comparison',
                'Claims analysis with loss triangle and reserves breakdown',
              ] : [
                'Executive summary with KPIs and trends',
                'Claims analysis by LOB and state',
                'Premium performance with year-over-year comparison',
                'Underwriting profitability breakdown',
              ]).map(suggestion => (
                <button key={suggestion} onClick={() => setAiBuildPrompt(suggestion)}
                  className="text-[10px] px-2.5 py-1 rounded-full border border-indigo-500/20 text-indigo-700 dark:text-indigo-400/70 hover:bg-indigo-500/10 hover:text-indigo-800 dark:hover:text-indigo-400 transition-colors">
                  {suggestion}
                </button>
              ))}
            </div>
            <Button
              onClick={aiBuildMode === 'report' ? handleAiBuildReport : handleGeneratePlan}
              disabled={aiBuildLoading || !aiBuildPrompt.trim() || !aiBuildModelId}
              className="w-full rounded-lg gap-2 bg-indigo-500 hover:bg-indigo-600 text-white"
              data-testid="ai-build-submit"
            >
              {aiBuildLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {aiBuildLoading
                ? (aiBuildMode === 'report' ? 'Building Report...' : 'Generating plan...')
                : (aiBuildMode === 'report' ? 'Build Report with AI' : 'Generate plan')}
            </Button>
          </div>
        )}

        {/* ── STEP 2 / Plan preview & edit (dashboard mode only) ── */}
        {aiBuildMode === 'dashboard' && aiBuildStep === 'review' && aiBuildPlan && (
          <div className="space-y-4 mt-2" data-testid="ai-build-plan-review">
            {/* Title + description (editable) */}
            <div className="space-y-2">
              <Input
                value={aiBuildPlan.title || ''}
                onChange={e => setAiBuildPlan(p => ({ ...p, title: e.target.value }))}
                className="text-base font-heading"
                data-testid="ai-plan-title"
              />
              <textarea
                value={aiBuildPlan.description || ''}
                onChange={e => setAiBuildPlan(p => ({ ...p, description: e.target.value }))}
                className="w-full rounded-lg border border-border/50 bg-muted/30 p-2 text-xs min-h-[44px] resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500/30"
                placeholder="Dashboard description"
                data-testid="ai-plan-description"
              />
              <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                {aiBuildPlan.audience && (
                  <span className="px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 uppercase tracking-wider font-medium">{aiBuildPlan.audience}</span>
                )}
                <span>{(aiBuildPlan.intents || []).length} intent{(aiBuildPlan.intents || []).length !== 1 ? 's' : ''}</span>
                {aiBuildVocab && (
                  <span className="text-muted-foreground/80">Grounded in {aiBuildVocab.measure_count} measures, {aiBuildVocab.dimension_count} dimensions</span>
                )}
              </div>
            </div>

            {/* Intent cards */}
            <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1" data-testid="ai-plan-intents">
              {(aiBuildPlan.intents || []).map((intent, idx) => {
                const KIND_META = {
                  kpi:          { color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30', label: 'KPI',          icon: TrendingUp },
                  trend:        { color: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',           label: 'Trend',        icon: LineChart },
                  breakdown:    { color: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30',    label: 'Breakdown',    icon: BarChart3 },
                  distribution: { color: 'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300 border-fuchsia-500/30', label: 'Distribution', icon: PieChart },
                  comparison:   { color: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',       label: 'Comparison',   icon: BarChart3 },
                  table:        { color: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30',       label: 'Table',        icon: Table },
                };
                const meta = KIND_META[intent.kind] || KIND_META.breakdown;
                const KindIcon = meta.icon;
                return (
                  <div key={idx} className="rounded-lg border border-border/40 bg-muted/10 p-3 flex items-start gap-3 group" data-testid={`ai-plan-intent-${idx}`}>
                    <div className={`w-8 h-8 rounded-md ${meta.color} border flex items-center justify-center flex-shrink-0`}>
                      <KindIcon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select
                          value={intent.kind}
                          onValueChange={(value) => {
                            setAiBuildPlan(p => ({
                              ...p,
                              intents: p.intents.map((it, i) => i === idx
                                ? { ...it, kind: value, time_grain: value === 'kpi' ? null : it.time_grain, group_by: value === 'kpi' ? '' : it.group_by }
                                : it),
                            }));
                          }}
                        >
                          <SelectTrigger className="h-6 px-2 text-[10px] w-auto min-w-[110px] border-border/40 bg-muted/30" data-testid={`ai-plan-intent-${idx}-kind`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(KIND_META).map(([k, m]) => (
                              <SelectItem key={k} value={k}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {intent.measure && (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 font-mono truncate max-w-[160px]" title={intent.measure}>
                            {intent.measure}
                          </span>
                        )}
                        {intent.group_by && (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-muted/40 text-muted-foreground font-mono truncate max-w-[140px]" title={`by ${intent.group_by}`}>
                            by {intent.group_by}
                          </span>
                        )}
                        {intent.time_grain && (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-muted/40 text-muted-foreground uppercase tracking-wider">{intent.time_grain}</span>
                        )}
                        {intent.top_n && (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300">Top {intent.top_n}</span>
                        )}
                      </div>
                      <Input
                        value={intent.intent || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setAiBuildPlan(p => ({ ...p, intents: p.intents.map((it, i) => i === idx ? { ...it, intent: value } : it) }));
                        }}
                        placeholder="What should this tile show?"
                        className="h-7 text-xs bg-transparent border-border/30"
                        data-testid={`ai-plan-intent-${idx}-text`}
                      />
                    </div>
                    <button
                      onClick={() => setAiBuildPlan(p => ({ ...p, intents: p.intents.filter((_, i) => i !== idx) }))}
                      className="opacity-40 hover:opacity-100 hover:text-destructive transition-all flex-shrink-0 p-1"
                      title="Remove intent"
                      data-testid={`ai-plan-intent-${idx}-remove`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
              {/* Add new intent */}
              <button
                onClick={() => setAiBuildPlan(p => ({
                  ...p,
                  intents: [...(p.intents || []), { kind: 'breakdown', intent: '', measure: '', group_by: '', time_grain: null, top_n: null, filters: [] }],
                }))}
                className="w-full rounded-lg border border-dashed border-border/40 bg-muted/5 hover:bg-muted/15 hover:border-indigo-500/30 transition-colors py-2.5 text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5"
                data-testid="ai-plan-add-intent"
              >
                <Plus className="w-3.5 h-3.5" />
                Add another intent
              </button>
            </div>

            {/* Footer actions */}
            <div className="flex items-center gap-2 pt-1">
              <Button
                variant="ghost"
                onClick={() => { setAiBuildStep('compose'); }}
                disabled={aiBuildLoading}
                className="text-xs text-muted-foreground hover:text-foreground"
                data-testid="ai-plan-back-btn"
              >
                ← Edit prompt
              </Button>
              <div className="flex-1" />
              <Button
                onClick={handleAiBuildDashboard}
                disabled={aiBuildLoading || !(aiBuildPlan.intents || []).length}
                className="rounded-lg gap-2 bg-indigo-500 hover:bg-indigo-600 text-white"
                data-testid="ai-plan-build-btn"
              >
                {aiBuildLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {aiBuildLoading ? `Building ${(aiBuildPlan.intents || []).length} visuals...` : `Build dashboard (${(aiBuildPlan.intents || []).length} visuals)`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    {/* Import — source picker */}
    <Dialog open={importPopupOpen} onOpenChange={setImportPopupOpen}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg" data-testid="import-source-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-orange-400" /> Import a report</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-sm text-muted-foreground -mt-1">
          Choose where your report is coming from. We'll read the file and map it to one of your semantic models.
        </DialogDescription>
        <div className="grid grid-cols-2 gap-3 mt-2">
          {IMPORT_SOURCES.map(s => (
            <button key={s.id}
              onClick={() => { setImportPopupOpen(false); setImportSource(s.id); setImportParsed(null); setImportModelId(''); }}
              className="flex flex-col items-start gap-1 p-4 rounded-xl border border-border/40 bg-muted/10 hover:border-primary/40 hover:bg-primary/5 text-left transition-all active:scale-95"
              data-testid={`import-source-${s.id}`}>
              <FileText className={`w-6 h-6 ${s.color}`} />
              <span className="text-sm font-medium mt-1">{s.name}</span>
              <span className="text-[10px] text-muted-foreground">{s.hint}</span>
            </button>
          ))}
          <button
            onClick={() => { setImportPopupOpen(false); setPyramidImportOpen(true); }}
            className="flex flex-col items-start gap-1 p-4 rounded-xl border border-border/40 bg-muted/10 hover:border-primary/40 hover:bg-primary/5 text-left transition-all active:scale-95"
            data-testid="import-source-pyramid">
            <Pyramid className="w-6 h-6 text-amber-400" />
            <span className="text-sm font-medium mt-1">Pyramid Analytics</span>
            <span className="text-[10px] text-muted-foreground">Read a Pyramid content repository and rebuild a report or dashboard.</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Import — upload / parse / generate for the chosen source */}
    {(() => {
      const src = IMPORT_SOURCES.find(s => s.id === importSource);
      return (
        <Dialog open={!!importSource} onOpenChange={v => { if (!v) { setImportSource(null); setImportParsed(null); setImportModelId(''); } }}>
          <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg" data-testid="import-file-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileText className={`w-5 h-5 ${src?.color || 'text-orange-400'}`} /> Import {src?.name} report</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              {!importParsed ? (
                <>
                  <p className="text-sm text-muted-foreground">Upload a <code>{src?.accept}</code> file to import it into the platform.</p>
                  <div className="border-2 border-dashed border-border/30 rounded-xl p-8 text-center">
                    <input type="file" accept={src?.accept} onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setImportParsing(true);
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('source', importSource);
                        const result = await api.parseBiImport(formData);
                        if (result.error || result.detail) { toast.error(result.error || result.detail); return; }
                        setImportParsed(result);
                        toast.success(`Parsed: ${result.summary?.body_elements || 0} elements found`);
                      } catch (err) { toast.error(err.message || 'Parse failed'); }
                      finally { setImportParsing(false); }
                    }} className="hidden" id="import-file-input" data-testid="import-file-input" />
                    <label htmlFor="import-file-input" className="cursor-pointer">
                      {importParsing ? <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-2" /> : <Upload className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />}
                      <p className="text-sm text-muted-foreground">{importParsing ? 'Parsing…' : `Click to upload ${src?.accept} file`}</p>
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div className="p-3 bg-muted/20 rounded-lg text-xs space-y-1">
                    <p className="font-medium text-foreground">{importParsed.file_name}</p>
                    <div className="flex gap-3 text-muted-foreground">
                      <span>{importParsed.summary?.data_sets || 0} data sets</span>
                      <span>{importParsed.summary?.body_elements || 0} elements</span>
                      <span>{importParsed.summary?.fields || 0} fields</span>
                    </div>
                  </div>
                  {importParsed.body_elements?.length > 0 && (
                    <div className="max-h-32 overflow-auto space-y-1">
                      {importParsed.body_elements.map((el, i) => (
                        <div key={`${el.type}-${el.name || el.chart_type || 'unnamed'}-${i}`} className="flex items-center gap-2 text-xs px-2 py-1 bg-muted/10 rounded">
                          <Badge variant="outline" className="text-[8px]">{el.type}</Badge>
                          <span className="truncate">{el.name || el.chart_type || 'Unnamed'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div>
                    <Label className="text-sm">Map to Semantic Model</Label>
                    <Select value={importModelId} onValueChange={setImportModelId}>
                      <SelectTrigger className="mt-1" data-testid="import-model-select"><SelectValue placeholder="Select model…" /></SelectTrigger>
                      <SelectContent>{models.map(m => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button onClick={async () => {
                    if (!importModelId) { toast.error('Select a model'); return; }
                    setImporting(true);
                    try {
                      const result = await api.generateBiImport({ session_id: importParsed.session_id, model_id: importModelId });
                      toast.success(`Report created with ${result.visuals_created} visuals`);
                      setImportSource(null);
                      setImportParsed(null);
                      navigate(`/dashboards/builder?id=${result.report_id}&mode=report`);
                    } catch (err) { toast.error(err.message); }
                    finally { setImporting(false); }
                  }} disabled={importing || !importModelId} className="w-full gap-2 bg-primary text-primary-foreground hover:opacity-90" data-testid="import-generate-btn">
                    {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                    {importing ? 'Importing…' : 'Import Report'}
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      );
    })()}

    <Dialog open={catalogOpen} onOpenChange={setCatalogOpen}>
      <DialogContent className="max-w-lg" data-testid="catalog-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-primary" /> Generate Insight Catalog</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-sm text-muted-foreground -mt-1">
          A PDF with one documented page per insight — purpose, fields, data lineage and an example screenshot.
        </DialogDescription>
        <div className="space-y-3 mt-2">
          <div className="flex items-center justify-between text-sm">
            <span>{catalogSel.length === 0 ? `All insights (${reports.length})` : `${catalogSel.length} selected`}</span>
            {catalogSel.length >= reports.length && reports.length > 0
              ? <button onClick={() => setCatalogSel([])} className="text-xs text-primary hover:underline" data-testid="catalog-clear-all">Clear all</button>
              : <button onClick={() => setCatalogSel(reports.map(r => r.report_id))} className="text-xs text-primary hover:underline" data-testid="catalog-select-all">Select all</button>}
          </div>
          <div className="max-h-64 overflow-y-auto border border-border/50 rounded-lg divide-y divide-border/40" data-testid="catalog-report-list">
            {reports.map(r => {
              const checked = catalogSel.includes(r.report_id);
              return (
                <label key={r.report_id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted/30" data-testid={`catalog-item-${r.report_id}`}>
                  <input type="checkbox" checked={checked} onChange={() => setCatalogSel(prev => checked ? prev.filter(x => x !== r.report_id) : [...prev, r.report_id])} />
                  <span className="truncate">{r.name}</span>
                </label>
              );
            })}
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input type="checkbox" checked={catalogAi} onChange={e => setCatalogAi(e.target.checked)} data-testid="catalog-ai-toggle" />
            Include AI purpose &amp; use explanations (slower)
          </label>
          <Button onClick={generateCatalog} disabled={catalogLoading} className="w-full" data-testid="catalog-generate-btn">
            {catalogLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</> : <><FileDown className="w-4 h-4 mr-2" />Generate catalog PDF</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </React.Fragment>
  );
}
